import React from 'react';
import { Box, Card, CardActionArea, CardContent, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    minHeight: theme.spacing(18.5),
    color: theme.palette.dashboardCard.color,
    background: theme.palette.dashboardCard.background,
    border: `1px solid ${theme.palette.dashboardCard.border}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 1,
      background: theme.palette.dashboardCard.topRuleBackground,
    },
  },
  action: {
    height: '100%',
    color: theme.palette.dashboardCard.color,
    '&:hover $content': {
      background: theme.palette.dashboardCard.hoverBackground,
    },
  },
  content: {
    minHeight: theme.spacing(18.5),
    height: '100%',
    padding: theme.spacing(2, 2.5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
    transition: theme.transitions.create('background', {
      duration: theme.transitions.duration.shorter,
    }),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  label: {
    color: theme.palette.dashboardCard.label,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold as number,
    lineHeight: 1.3,
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  valueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  value: {
    color: theme.palette.dashboardCard.value,
    fontSize: theme.typography.pxToRem(32),
    fontWeight: theme.typography.fontWeightBold as number,
    lineHeight: 1.05,
    letterSpacing: 0,
  },
  successIcon: {
    color: theme.palette.dashboardCard.success,
    fontSize: theme.typography.pxToRem(18),
    flexShrink: 0,
  },
  detail: {
    color: theme.palette.dashboardCard.detail,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.35,
    letterSpacing: 0,
    marginTop: theme.spacing(1.5),
  },
}));

interface DashboardCardProps {
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
  onClick?: () => void;
  success?: boolean;
}

const DashboardCard = ({ label, value, detail, onClick, success = false }: DashboardCardProps) => {
  const classes = useStyles();
  const content = (
    <CardContent className={classes.content}>
      <Box>
        <Typography className={classes.label}>{label}</Typography>
        <Box className={classes.valueRow}>
          <Typography component="div" className={classes.value}>
            {value}
          </Typography>
          {success && <CheckCircleIcon className={classes.successIcon} />}
        </Box>
      </Box>
      {detail ? (
        <Typography component="div" className={classes.detail}>
          {detail}
        </Typography>
      ) : null}
    </CardContent>
  );

  return (
    <Card className={classes.card}>
      {onClick ? (
        <CardActionArea className={classes.action} onClick={onClick}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
};

export default DashboardCard;
